<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'
import { useSeo } from '@/composables/useSeo'
import NavbarPublic from '@/components/NavbarPublic.vue'
import IfcViewer from '@/components/IfcViewer.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import { SAMPLE_MODEL } from '@/samples'
import GoogleIcon from '@/components/GoogleIcon.vue'

const { loginWithGoogle, loginPending, currentUser, isRegistered } = useAuth()
const { t } = useI18n()

useSeo(() => ({
  title: t('seo.landingTitle'),
  description: t('seo.landingDesc'),
  path: '/',
}))

// Shown under the hero; the full list lives on How it works (#formats)
const formatGroups = [
  { label: 'BIM', items: ['RVT', 'IFC', 'Tekla', 'DXF', 'BTLX', 'DSTV'] },
  { label: 'CAD', items: ['STEP', 'IGES', '3DM', 'FCStd', 'BREP'] },
  { label: '3D', items: ['GLB', 'OBJ', 'FBX', 'DAE', 'STL', '3MF', 'USDZ', 'PLY', '+40'] },
]

async function handleLogin() {
  try {
    await loginWithGoogle()
  } catch (e: any) {
    if (e.code !== 'auth/cancelled-popup-request' && e.code !== 'auth/popup-closed-by-user') {
      console.error('Login failed:', e)
    }
  }
}
</script>

<template>
  <div class="ui-page landing">
    <NavbarPublic :show-login-button="false" />

    <div class="page-backdrop" aria-hidden="true"></div>

    <!-- Hero -->
    <section class="hero">
      <div class="hero-grid">
        <div class="hero-copy">
          <span class="eyebrow">
            <span class="eyebrow-dot"></span>
            {{ t('landing.eyebrow') }}
          </span>
          <h1>
            {{ t('landing.heroTitle1') }}<br />
            <span class="gradient-text">{{ t('landing.heroTitle2') }}</span>
          </h1>
          <p class="hero-subtitle">{{ t('landing.heroSubtitle') }}</p>

          <div class="hero-actions">
            <router-link to="/upload" class="ui-btn ui-btn-primary ui-btn-lg">
              <span class="mdi mdi-plus"></span>
              {{ t('landing.ctaUpload') }}
            </router-link>
            <router-link v-if="currentUser" to="/files" class="ui-btn ui-btn-ghost ui-btn-lg">
              <span class="mdi mdi-folder-outline"></span>
              {{ t('nav.files') }}
            </router-link>
            <button
              v-if="!isRegistered"
              class="ui-btn ui-btn-ghost ui-btn-lg"
              :disabled="loginPending"
              @click="handleLogin"
            >
              <GoogleIcon :size="18" />
              {{ t('nav.signInGoogle') }}
            </button>
          </div>
          <p class="hero-hint">
            <span class="mdi mdi-check-circle-outline"></span> {{ t('landing.ctaNoAccount') }}
            <span class="sep">·</span>
            {{ t('landing.heroFree') }}
          </p>
          <router-link to="/how-it-works" class="how-link">
            {{ t('nav.howItWorks') }} <span class="mdi mdi-arrow-right"></span>
          </router-link>
        </div>

        <!-- Stylized 3D stage -->
        <div class="stage">
          <div class="stage-glow" aria-hidden="true"></div>
          <div class="stage-card">
            <div class="stage-backdrop" aria-hidden="true">
              <div class="stage-sky"></div>
              <div class="stage-lines"></div>
              <div class="stage-sun"></div>
            </div>
            <span class="tick tl" aria-hidden="true"></span>
            <span class="tick tr" aria-hidden="true"></span>
            <span class="tick bl" aria-hidden="true"></span>
            <span class="tick br" aria-hidden="true"></span>
            <div class="stage-viewer">
              <IfcViewer
                :url="SAMPLE_MODEL.url"
                :file-name="SAMPLE_MODEL.fileName"
                :auto-rotate="true"
                :minimal="true"
              />
            </div>
            <div class="stage-caption">
              <span class="live-dot"></span>
              {{ t('landing.viewerCaption') }}
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- Supported formats -->
    <section class="formats-strip">
      <p class="formats-title">{{ t('landing.formatsTitle') }}</p>
      <div class="format-groups">
        <div v-for="g in formatGroups" :key="g.label" class="format-group">
          <span class="group-label">{{ g.label }}</span>
          <span v-for="f in g.items" :key="f" class="fmt">{{ f }}</span>
        </div>
      </div>
      <router-link to="/how-it-works#formats" class="how-link">
        {{ t('landing.formatsLink') }} <span class="mdi mdi-arrow-right"></span>
      </router-link>
    </section>

    <SiteFooter />
  </div>
</template>

<style scoped>
.landing {
  position: relative;
  overflow-x: hidden;
}

.page-backdrop {
  position: absolute;
  inset: 0 0 auto 0;
  height: 900px;
  pointer-events: none;
  background-image:
    radial-gradient(45% 50% at 70% 10%, var(--accent-soft), transparent 70%),
    linear-gradient(var(--grid-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
  background-size: 100% 100%, 56px 56px, 56px 56px;
  mask-image: linear-gradient(to bottom, #000 30%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 30%, transparent 100%);
}

/* Hero */
.hero {
  position: relative;
  padding: 8rem 1.25rem 4rem;
}

.hero-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  gap: 3.5rem;
  align-items: center;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  margin-bottom: 1.5rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--text-muted);
  background: var(--surface);
  border: 1px solid var(--border);
}

.eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-2);
  box-shadow: 0 0 10px var(--accent-2);
}

.hero h1 {
  font-size: clamp(2.4rem, 5vw, 3.75rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.045em;
  margin: 0 0 1.25rem;
}

.gradient-text {
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.1rem;
  color: var(--text-muted);
  line-height: 1.6;
  margin: 0 0 2rem;
  max-width: 520px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hero-hint {
  margin: 1.1rem 0 0;
  font-size: 0.82rem;
  color: var(--text-subtle);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.hero-hint .mdi {
  color: var(--success);
}

.sep {
  color: var(--text-faint);
}

.how-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 1.5rem;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--accent-text);
  text-decoration: none;
}

.how-link .mdi {
  transition: transform 0.2s var(--ease);
}

.how-link:hover .mdi {
  transform: translateX(3px);
}

/* Format strip */
.formats-strip {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1.25rem 4rem;
  text-align: center;
}

.formats-title {
  margin: 0 0 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.format-groups {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem 1.5rem;
}

.format-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.group-label {
  margin-right: 0.25rem;
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--accent-text);
}

.fmt {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  padding: 0.25rem 0.55rem;
  border-radius: 6px;
  color: var(--text-muted);
  background: var(--surface);
  border: 1px solid var(--border);
}

/* Stage */
.stage {
  position: relative;
  height: 540px;
}

.stage-glow {
  position: absolute;
  inset: -10% -6%;
  background: radial-gradient(50% 50% at 50% 55%, var(--accent-soft), transparent 70%);
  filter: blur(30px);
  pointer-events: none;
}

.stage-card {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-xl);
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  isolation: isolate;
}

.stage-backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;
}

/* Sky: deep night-blue in dark, warm paper-grey in light */
.stage-sky {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(70% 55% at 50% 38%, var(--stage-haze), transparent 70%),
    linear-gradient(180deg, var(--stage-top) 0%, var(--stage-bottom) 100%);
}

/* Fine drafting grid behind the model (the floor grid is drawn in WebGL) */
.stage-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(var(--stage-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--stage-line) 1px, transparent 1px);
  background-size: 24px 24px;
  mask-image: radial-gradient(60% 50% at 50% 35%, #000, transparent 75%);
  -webkit-mask-image: radial-gradient(60% 50% at 50% 35%, #000, transparent 75%);
}

.stage-sun {
  position: absolute;
  top: -12%;
  right: -8%;
  width: 55%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(circle, var(--stage-sun), transparent 65%);
  filter: blur(10px);
}

.stage-viewer {
  position: absolute;
  inset: 0;
}

.stage-viewer :deep(.ifc-viewer-wrapper),
.stage-viewer :deep(.ifc-container) {
  min-height: 0;
}

.tick {
  position: absolute;
  width: 16px;
  height: 16px;
  border-color: var(--border-strong);
  pointer-events: none;
  z-index: 2;
}

.tick.tl { top: 14px; left: 14px; border-top: 1px solid; border-left: 1px solid; }
.tick.tr { top: 14px; right: 14px; border-top: 1px solid; border-right: 1px solid; }
.tick.bl { bottom: 14px; left: 14px; border-bottom: 1px solid; border-left: 1px solid; }
.tick.br { bottom: 14px; right: 14px; border-bottom: 1px solid; border-right: 1px solid; }

.stage-caption {
  position: absolute;
  left: 50%;
  bottom: 1.1rem;
  transform: translateX(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  white-space: nowrap;
  background: color-mix(in srgb, var(--surface) 70%, transparent);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--border);
  pointer-events: none;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--success);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--success) 60%, transparent); }
  70% { box-shadow: 0 0 0 7px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
}


/* Responsive */
@media (max-width: 960px) {
  .hero {
    padding-top: 6.5rem;
  }

  .hero-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    max-width: 640px;
  }

  .hero-copy {
    text-align: center;
  }

  .hero-subtitle {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-actions,
  .hero-hint {
    justify-content: center;
  }

  .stage {
    height: 380px;
  }
}

</style>
